import { useMemo, ReactNode } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";

// Table components
import TableTitle from "../TableTitle";
import SummaryTableCell from "../TableCell";

// Custom components
import CardSkeleton from "examples/Cards/CardSkeleton";
import CardError from "examples/Cards/CardError";

// Declaring props types for CountryTable
interface Props {
  icon?: {
    color?: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "light" | "dark";
    component: ReactNode;
  };
  title?: string;
  withTitleCell?: boolean;
  description?: string | ReactNode;
  rows?: {
    [key: string]: string | number | (string | number)[];
  }[];
  shadow?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  onRetry?: () => void;
}

function CountryTable({
  icon,
  title,
  withTitleCell,
  description,
  rows,
  shadow,
  isLoading = false,
  isError = false,
  errorMessage,
  onRetry,
}: Props): JSX.Element {
  const renderTableCells = () => {
    const tableRows: JSX.Element[] = [];

    if (withTitleCell && rows?.length) {
      const lineCells: any = [];
      Object.keys(rows[0]).map((cellTitle, i) => {
        lineCells.push(
          <SummaryTableCell
            key={`${cellTitle}-header`}
            content={i > 0 ? cellTitle : undefined}
            align="center"
          />
        );
      });
      tableRows.push(<TableRow key="header-row">{lineCells}</TableRow>);
    }

    rows.forEach((row: { [key: string]: string | number | (string | number)[] }, key: any) => {
      const lineCells: any = [];
      const rowKey = `row-${key}`;

      Object.entries(row).map(([cellTitle, cellContent]: any, i: number) =>
        lineCells.push(
          <SummaryTableCell
            key={`${cellTitle}-${cellContent}`}
            content={cellContent}
            titleRow={i === 0 ? cellContent : undefined}
            noBorder={key === rows.length - 1}
            align={Object.keys(row).length > 2 ? "center" : "left"}
          />
        )
      );

      tableRows.push(<TableRow key={rowKey}>{lineCells}</TableRow>);
    });

    return tableRows;
  };

  const tableCells = useMemo(() => renderTableCells(), [rows]);
  const skeletonHeight = Math.max((rows?.length || 8) * 36, 160);

  const renderTable = (
    <MDBox p={1}>
      <Accordion defaultExpanded sx={{ boxShadow: "none" }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {title || description ? (
            <TableTitle icon={icon} title={title} description={description} shadow={shadow} />
          ) : null}
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer sx={{ height: "100%", boxShadow: !shadow && "none" }}>
            <Table>
              <TableBody>{tableCells}</TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    </MDBox>
  );

  if (isLoading) {
    return <CardSkeleton height={skeletonHeight} />;
  }

  if (isError) {
    return <CardError height={skeletonHeight} message={errorMessage} onRetry={onRetry} />;
  }

  return title || description ? <Card>{renderTable}</Card> : renderTable;
}

// Declaring default props for CountryTable
CountryTable.defaultProps = {
  title: "",
  rows: [{}],
  shadow: true,
  isLoading: false,
  isError: false,
};

export default CountryTable;
